import React from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Description,
} from "@headlessui/react";

/**
 * Accessible modal shell (focus trap, Escape, aria-modal, labelledby/describedby).
 * @param {boolean} open
 * @param {(value?: boolean) => void} onClose — Headless UI may call with a boolean reason
 * @param {string} [title] — rendered as DialogTitle (sets aria-labelledby)
 * @param {React.ReactNode} [description] — rendered as Description (sets aria-describedby)
 * @param {'dialog' | 'alertdialog'} [role]
 * @param {string} [titleClassName]
 * @param {string} [descriptionClassName]
 * @param {string} [panelClassName] — width, padding, border (panel is always bg-white shadow)
 * @param {'center' | 'start'} [contentAlign] — vertical alignment of panel
 */
function ModalDialog({
  open,
  onClose,
  title,
  description,
  children,
  role = "dialog",
  titleClassName = "text-lg font-medium text-gray-900",
  descriptionClassName = "text-sm text-gray-600",
  panelClassName = "",
  contentAlign = "center",
}) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50" role={role}>
      <DialogBackdrop className="fixed inset-0 bg-gray-600 bg-opacity-50" />
      <div className="fixed inset-0 overflow-y-auto">
        <div
          className={
            contentAlign === "start"
              ? "flex min-h-full items-start justify-center pt-20 pb-8 px-4"
              : "flex min-h-full items-center justify-center p-4"
          }
        >
          <DialogPanel
            className={`relative bg-white shadow-xl rounded-md ${panelClassName}`}
          >
            {title != null && title !== "" ? (
              <DialogTitle className={titleClassName}>{title}</DialogTitle>
            ) : null}
            {description != null && description !== "" ? (
              <Description className={`mt-2 ${descriptionClassName}`}>
                {description}
              </Description>
            ) : null}
            {children}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export default ModalDialog;
